import { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import Dialog from '../common/Dialog';

type FaceDetectionResult = {
  boundingBox: {
    x: number;
    y: number;
    width: number;
    height: number;
  };
};

const FACE_API_SCRIPT_URL =
  'https://cdn.jsdelivr.net/npm/face-api.js@0.22.2/dist/face-api.min.js';
const FACE_API_MODEL_URL =
  'https://justadudewhohacks.github.io/face-api.js/models';

let faceApiScriptPromise: Promise<any> | null = null;
let faceApiModelsPromise: Promise<void> | null = null;

const loadFaceApiScript = async () => {
  if ((window as any).faceapi) return (window as any).faceapi;
  if (!faceApiScriptPromise) {
    faceApiScriptPromise = new Promise((resolve, reject) => {
      const existing = document.querySelector(
        'script[data-face-api-script="true"]'
      ) as HTMLScriptElement | null;
      if (existing) {
        existing.addEventListener('load', () =>
          resolve((window as any).faceapi)
        );
        existing.addEventListener('error', () =>
          reject(new Error('Failed to load face-api.js script'))
        );
        return;
      }

      const script = document.createElement('script');
      script.src = FACE_API_SCRIPT_URL;
      script.async = true;
      script.defer = true;
      script.setAttribute('data-face-api-script', 'true');
      script.onload = () => resolve((window as any).faceapi);
      script.onerror = () =>
        reject(new Error('Failed to load face-api.js script'));
      document.head.appendChild(script);
    });
  }

  return faceApiScriptPromise;
};

const ensureFaceApiModels = async (faceapi: any) => {
  if (faceapi?.nets?.tinyFaceDetector?.isLoaded) return;
  if (!faceApiModelsPromise) {
    faceApiModelsPromise =
      faceapi.nets.tinyFaceDetector.loadFromUri(FACE_API_MODEL_URL);
  }
  await faceApiModelsPromise;
};

interface FacialVerificationProps {
  isOpen: boolean;
  onClose: () => void;
  onCapture: (file: File, imageData?: string) => void;
  title?: string;
  videoHeight?: string;
  isLoading?: boolean;
}

const FacialVerification = ({
  isOpen,
  onClose,
  onCapture,
  title = 'Facial Verification',
  videoHeight = 'h-96',
  isLoading = false,
}: FacialVerificationProps) => {
  const [isCameraOpen, setIsCameraOpen] = useState(false);
  const [isFaceAligned, setIsFaceAligned] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const faceCheckBusyRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      handleFacialVerification();
    } else {
      stopCamera();
    }
  }, [isOpen]);

  const handleFacialVerification = async () => {
    try {
      setIsCameraOpen(true);
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          width: { ideal: 640 },
          height: { ideal: 480 },
          facingMode: 'user',
        },
      });
      setCameraStream(stream);
    } catch (error: any) {
      if (error.name === 'NotAllowedError') {
        toast.error(
          'Camera access denied. Please allow camera permissions in your browser settings.'
        );
      } else if (error.name === 'NotFoundError') {
        toast.error('No camera found. Please connect a camera and try again.');
      } else if (error.name === 'NotReadableError') {
        toast.error(
          'Camera is already in use by another application. Please close other apps using camera.'
        );
      } else if (error.name === 'NotSupportedError') {
        toast.error(
          'Camera not supported in this browser. Please try using Chrome, Firefox, or Edge.'
        );
      } else if (window.location.protocol === 'http:') {
        toast.error(
          'Camera access requires HTTPS. Please use a secure connection (https://).'
        );
      } else {
        toast.error(
          'Unable to access camera. Please check permissions and try again.'
        );
      }

      setIsCameraOpen(false);
      onClose();
    }
  };

  const stopCamera = () => {
    if (cameraStream) {
      cameraStream.getTracks().forEach(track => track.stop());
      setCameraStream(null);
    }

    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (video) {
      video.srcObject = null;
    }

    setIsCameraOpen(false);
    setIsFaceAligned(false);
  };

  const capturePhoto = async () => {
    if (!cameraStream) return;

    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video || !video.videoWidth || !video.videoHeight) {
      toast.error('Camera is not ready. Please try again.');
      return;
    }

    try {
      const frameCanvas = document.createElement('canvas');
      frameCanvas.width = video.videoWidth;
      frameCanvas.height = video.videoHeight;
      const frameCtx = frameCanvas.getContext('2d');
      if (!frameCtx) {
        toast.error('Unable to process camera frame.');
        return;
      }
      frameCtx.drawImage(video, 0, 0, frameCanvas.width, frameCanvas.height);

      let faces: FaceDetectionResult[] = [];
      const FaceDetectorCtor = (window as any).FaceDetector;
      if (FaceDetectorCtor) {
        const detector = new FaceDetectorCtor({
          fastMode: true,
          maxDetectedFaces: 2,
        });
        faces = (await detector.detect(frameCanvas)) as FaceDetectionResult[];
      } else {
        try {
          const faceapi = await loadFaceApiScript();
          await ensureFaceApiModels(faceapi);
          const detections = await faceapi.detectAllFaces(
            frameCanvas,
            new faceapi.TinyFaceDetectorOptions({
              inputSize: 416,
              scoreThreshold: 0.4,
            })
          );
          faces = detections.map((d: any) => ({
            boundingBox: {
              x: d.box.x,
              y: d.box.y,
              width: d.box.width,
              height: d.box.height,
            },
          }));
        } catch (_fallbackError) {
          toast.error('Face detection setup failed. Check network and retry.');
          return;
        }
      }

      if (!faces.length) {
        toast.error('No face detected. Keep face inside oval and retry.');
        return;
      }

      if (faces.length > 1) {
        toast.error('Multiple faces detected. Keep only one face in frame.');
        return;
      }

      const face = faces[0].boundingBox;
      const faceCenterX = face.x + face.width / 2;
      const faceCenterY = face.y + face.height / 2;
      const ovalCenterX = frameCanvas.width / 2;
      const ovalCenterY = frameCanvas.height / 2;
      const ovalRadiusX = (frameCanvas.width * 120) / 640;
      const ovalRadiusY = (frameCanvas.height * 160) / 480;
      const ovalDistance =
        (faceCenterX - ovalCenterX) ** 2 / ovalRadiusX ** 2 +
        (faceCenterY - ovalCenterY) ** 2 / ovalRadiusY ** 2;

      if (ovalDistance > 1) {
        toast.error('Center your face in the dotted oval and retry.');
        return;
      }

      if (
        face.width < frameCanvas.width * 0.14 ||
        face.height < frameCanvas.height * 0.2
      ) {
        toast.error('Face is too small. Move closer to the camera.');
        return;
      }

      const padX = face.width * 0.25;
      const padTop = face.height * 0.35;
      const padBottom = face.height * 0.3;
      const cropX = Math.max(0, face.x - padX);
      const cropY = Math.max(0, face.y - padTop);
      const cropW = Math.min(frameCanvas.width - cropX, face.width + padX * 2);
      const cropH = Math.min(
        frameCanvas.height - cropY,
        face.height + padTop + padBottom
      );

      const outputCanvas = document.createElement('canvas');
      outputCanvas.width = 512;
      outputCanvas.height = 512;
      const outputCtx = outputCanvas.getContext('2d');
      if (!outputCtx) {
        toast.error('Unable to process captured photo.');
        return;
      }

      outputCtx.drawImage(
        frameCanvas,
        cropX,
        cropY,
        cropW,
        cropH,
        0,
        0,
        outputCanvas.width,
        outputCanvas.height
      );

      const imageData = outputCanvas.toDataURL('image/jpeg', 0.92);

      const byteString = atob(imageData.split(',')[1]);
      const mimeString = imageData.split(',')[0].split(':')[1].split(';')[0];
      const ab = new ArrayBuffer(byteString.length);
      const ia = new Uint8Array(ab);
      for (let i = 0; i < byteString.length; i++) {
        ia[i] = byteString.charCodeAt(i);
      }
      const blob = new Blob([ab], { type: mimeString });
      const file = new File([blob], 'facial-verification.jpg', {
        type: 'image/jpeg',
      });

      onCapture(file, imageData);
      stopCamera();
      onClose();
    } catch (_error) {
      toast.error('Photo capture failed. Please try again.');
    }
  };

  useEffect(() => {
    return () => {
      if (cameraStream) {
        cameraStream.getTracks().forEach(track => track.stop());
      }
    };
  }, [cameraStream]);

  useEffect(() => {
    if (cameraStream && isCameraOpen) {
      const video = document.getElementById('camera-video') as HTMLVideoElement;
      if (video) {
        video.srcObject = cameraStream;
        video.play().catch(error => {
          console.error('Error playing video:', error);
        });
      }
    }
  }, [cameraStream, isCameraOpen]);

  useEffect(() => {
    if (!isCameraOpen || !cameraStream) {
      setIsFaceAligned(false);
      return;
    }

    let cancelled = false;
    const video = document.getElementById('camera-video') as HTMLVideoElement;
    if (!video) return;

    const FaceDetectorCtor = (window as any).FaceDetector;
    const detector = FaceDetectorCtor
      ? new FaceDetectorCtor({ fastMode: true, maxDetectedFaces: 2 })
      : null;

    const checkFaceStatus = async () => {
      if (cancelled || faceCheckBusyRef.current) return;
      if (!video.videoWidth || !video.videoHeight || video.readyState < 2) {
        return;
      }

      faceCheckBusyRef.current = true;
      try {
        const frameCanvas = document.createElement('canvas');
        frameCanvas.width = video.videoWidth;
        frameCanvas.height = video.videoHeight;
        const frameCtx = frameCanvas.getContext('2d');
        if (!frameCtx) {
          if (!cancelled) setIsFaceAligned(false);
          return;
        }
        frameCtx.drawImage(video, 0, 0, frameCanvas.width, frameCanvas.height);

        let faces: FaceDetectionResult[] = [];
        if (detector) {
          faces = (await detector.detect(frameCanvas)) as FaceDetectionResult[];
        } else {
          try {
            const faceapi = await loadFaceApiScript();
            await ensureFaceApiModels(faceapi);
            const detections = await faceapi.detectAllFaces(
              frameCanvas,
              new faceapi.TinyFaceDetectorOptions({
                inputSize: 416,
                scoreThreshold: 0.4,
              })
            );
            faces = detections.map((d: any) => ({
              boundingBox: {
                x: d.box.x,
                y: d.box.y,
                width: d.box.width,
                height: d.box.height,
              },
            }));
          } catch (_fallbackError) {
            faces = [];
          }
        }

        if (cancelled) return;
        if (faces.length !== 1) {
          setIsFaceAligned(false);
          return;
        }

        const face = faces[0].boundingBox;
        const faceCenterX = face.x + face.width / 2;
        const faceCenterY = face.y + face.height / 2;
        const ovalCenterX = frameCanvas.width / 2;
        const ovalCenterY = frameCanvas.height / 2;
        const ovalRadiusX = (frameCanvas.width * 120) / 640;
        const ovalRadiusY = (frameCanvas.height * 160) / 480;
        const ovalDistance =
          (faceCenterX - ovalCenterX) ** 2 / ovalRadiusX ** 2 +
          (faceCenterY - ovalCenterY) ** 2 / ovalRadiusY ** 2;

        const validSize =
          face.width >= frameCanvas.width * 0.14 &&
          face.height >= frameCanvas.height * 0.2;

        setIsFaceAligned(ovalDistance <= 1 && validSize);
      } catch (_error) {
        if (!cancelled) setIsFaceAligned(false);
      } finally {
        faceCheckBusyRef.current = false;
      }
    };

    void checkFaceStatus();
    const intervalId = window.setInterval(() => {
      void checkFaceStatus();
    }, 500);

    return () => {
      cancelled = true;
      window.clearInterval(intervalId);
      faceCheckBusyRef.current = false;
      setIsFaceAligned(false);
    };
  }, [isCameraOpen, cameraStream]);

  if (!isOpen) return null;

  return (
    <Dialog
      isOpen={isOpen}
      onClose={() => {
        stopCamera();
        onClose();
      }}
      title={title}
      maxWidth="2xl"
      onSubmit={capturePhoto}
      submitText="Capture Photo"
      disabled={!isFaceAligned || isLoading}
      onCancel={() => {
        stopCamera();
        onClose();
      }}
      cancelText="Cancel"
      isLoading={isLoading}
      closeOnOutsideClick={false}
    >
      <div className="relative bg-black rounded-lg overflow-hidden">
        <div className="relative">
          <video
            id="camera-video"
            autoPlay
            playsInline
            className={`w-full ${videoHeight} object-cover`}
            style={{ transform: 'scaleX(-1)' }}
          />

          <div className="absolute inset-0 pointer-events-none">
            <svg
              className="w-full h-full"
              viewBox="0 0 640 480"
              preserveAspectRatio="xMidYMid slice"
            >
              <defs>
                <mask id="oval-mask">
                  <rect width="640" height="480" fill="white" />
                  <ellipse cx="320" cy="240" rx="125" ry="165" fill="black" />
                </mask>
              </defs>

              <rect
                width="640"
                height="480"
                fill="white"
                mask="url(#oval-mask)"
              />

              <ellipse
                cx="320"
                cy="240"
                rx="120"
                ry="160"
                fill="none"
                stroke={isFaceAligned ? '#10b981' : '#ef4444'}
                strokeWidth="4"
                strokeDasharray="25,5"
              />

              <line
                x1="320"
                y1="80"
                x2="320"
                y2="400"
                stroke="#ef4444"
                strokeWidth="1"
                strokeDasharray="2,2"
                opacity="0.5"
              />
              <line
                x1="80"
                y1="240"
                x2="560"
                y2="240"
                stroke="#ef4444"
                strokeWidth="1"
                strokeDasharray="2,2"
                opacity="0.5"
              />
            </svg>
          </div>
        </div>
      </div>

      <div className="mt-4 text-xs text-muted-foreground text-center">
        <p className={isFaceAligned ? 'text-green-600' : 'text-red-500'}>
          {isFaceAligned
            ? 'Face is detected clearly. You can capture now.'
            : 'Face not detected clearly. Align face in oval with good lighting.'}
        </p>
      </div>
    </Dialog>
  );
};
export default FacialVerification;
